<template>
  <div>
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-card class="page-card">
        <v-card-title class="mx-3">
          {{ $t("CRUD.editInfo", [$t("App.personal")]) }}
        </v-card-title>
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
        <v-card-text>
          <v-container>
            <ValidationObserver ref="observer">
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.name") + $t("Global.colon") }}
                  <Required :show="true" mode="edit" />
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.name')"
                    rules="required"
                  >
                    <v-text-field
                      single-line
                      v-model="selfUser.name"
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'name')
                          .concat(errors)
                      "
                    >
                      <template v-slot:message="{ key, message }">
                        <div :class="key == 0 ? 'danger--text' : ''">
                          {{ message }}
                        </div>
                      </template>
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.number") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7">
                  {{ selfUser.job_number }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("AccountUser.Fields.organization") + $t("Global.colon")
                  }}
                </v-col>
                <v-col cols="9" md="7" v-if="selfUser.type != 1">
                  {{
                    selfUser && selfUser.organization
                      ? selfUser.organization.name
                      : ""
                  }}
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ selfUser.name }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.position") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7" v-if="selfUser.type != 1">
                  {{ selfUser.position }}
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ selfUser.name }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.role") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7" v-if="selfUser.type != 1">
                  {{ selfUser && selfUser.role ? selfUser.role.role_name : "" }}
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ selfUser.name }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.sex") + $t("Global.colon") }}
                  <Required :show="true" mode="edit" />
                </v-col>
                <v-col cols="9" md="7">
                  <v-radio-group row v-model="selfUser.sex">
                    <v-radio
                      v-for="s in sex"
                      :key="s.value"
                      :label="`${s.text}`"
                      :value="s.value"
                    ></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.mobile") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.mobile')"
                    rules="mobile"
                  >
                    <v-text-field
                      v-model="selfUser.mobile"
                      single-line
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'mobile')
                          .concat(errors)
                      "
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.email") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.email')"
                    rules="email"
                  >
                    <v-text-field
                      v-model="selfUser.email"
                      single-line
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'email')
                          .concat(errors)
                      "
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.login_name") + $t("Global.colon") }}
                  <Required :show="true" mode="edit" />
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.login_name')"
                    rules="required"
                  >
                    <v-text-field
                      single-line
                      v-model="selfUser.account_user_id"
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'account_user_id')
                          .concat(errors)
                      "
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("AccountUser.Fields.old_password") + $t("Global.colon")
                  }}
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.old_password')"
                    :rules="selfUser.new_password ? 'required|min:6' : 'min:6'"
                  >
                    <v-text-field
                      single-line
                      v-model="selfUser.old_password"
                      :error-messages="errors"
                      :append-icon="showOldPassword ? 'fa-eye' : 'fa-eye-slash'"
                      :type="showOldPassword ? 'text' : 'password'"
                      @click:append="showOldPassword = !showOldPassword"
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("AccountUser.Fields.new_password") + $t("Global.colon")
                  }}
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.new_password')"
                    rules="min:6"
                  >
                    <v-text-field
                      single-line
                      v-model="selfUser.new_password"
                      :error-messages="errors"
                      :append-icon="showNewPassword ? 'fa-eye' : 'fa-eye-slash'"
                      :type="showNewPassword ? 'text' : 'password'"
                      @click:append="showNewPassword = !showNewPassword"
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("AccountUser.Fields.cfm_password") + $t("Global.colon")
                  }}
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.cfm_password')"
                    :rules="cfmPassword()"
                  >
                    <v-text-field
                      single-line
                      v-model="selfUser.cfm_password"
                      :error-messages="
                        errors.length == 0
                          ? []
                          : [$t('AccountUser.Fields.cfm_password_error')]
                      "
                      :append-icon="showCfmPassword ? 'fa-eye' : 'fa-eye-slash'"
                      :type="showCfmPassword ? 'text' : 'password'"
                      @click:append="showCfmPassword = !showCfmPassword"
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
            </ValidationObserver>
          </v-container>
        </v-card-text>
      </v-card>
      <v-sheet class="page-opts">
        <v-card>
          <v-card-title>
            <v-row dense>
              <v-col cols="12">
                <v-btn
                  color="success"
                  block
                  :disabled="loading && !selfUser.id"
                  @click="submit()"
                >
                  <v-icon small class="pe-1">fa-save</v-icon>
                  {{ $t("CRUD.saveInfo", [$t("App.personal")]) }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
        </v-card>
      </v-sheet>
    </v-sheet>
  </div>
</template>
<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import Required from "@/components/plugins/Required";
import Consts from "@/consts.js";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import AccountUsers from "@/api/account_users";

export default {
  name: "AccountUserDetail",

  props: ["value", "mode"],

  components: {
    Required,
    Breadcrumbs,
    ValidationObserver,
    ValidationProvider,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("SettingNav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("App.myAccount"),
        disabled: true,
        href: "/my-self",
      },
    ],
    sex() {
      return Consts.account_user.sex;
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "accountUser"]),
  },

  data: () => ({
    selfUser: {},
    serverErrors: [],
    showOldPassword: false,
    showNewPassword: false,
    showCfmPassword: false,
  }),

  methods: {
    cfmPassword() {
      return { equal: this.selfUser.new_password };
    },
    submit() {
      this.$store.dispatch("fullscreenLoading");
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            var selfUser = JSON.parse(JSON.stringify(this.selfUser));
            delete selfUser.organization;
            delete selfUser.role;

            AccountUsers.editSelf(selfUser)
              .then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$refs.observer.reset();
                    this.$snackbar.success(
                      i18n.t("CRUD.success", [
                        i18n.t("CRUD.editInfo", [i18n.t("App.personal")]),
                      ])
                    );
                  } else if (
                    response.data.error &&
                    response.data.error.data &&
                    "ERR333" == response.data.error.code
                  ) {
                    this.serverErrors = response.data.error.data;
                  }
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              )
              .catch(
                function () {
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              );
          } else {
            this.$store.dispatch("fullscreenLoadingStop");
          }
        }.bind(this)
      );
    },
  },

  watch: {
    accountUser: {
      immediate: true, //初始赋值也调用
      handler() {
        this.selfUser = JSON.parse(JSON.stringify(this.accountUser));
      },
    },
  },
};
</script>